import { Modal, Form, InputNumber, Row, Col, Typography, Grid } from "antd";
import { SaveOutlined, CloseOutlined, PercentageOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import type { TfsSrpYearRatesData } from "../../helpers/interfaces/TfsSrpYearRatesInterfaces";

const { Text } = Typography;

type TfsSrpYearRateModalComponentProps = {
  open: boolean;
  record: TfsSrpYearRatesData | null;
  onCancel: () => void;
  onSave: (values: any) => void;
  loading?: boolean;
};

export const TfsSrpYearRateModalComponent = ({
  open,
  record,
  onCancel,
  onSave,
  loading = false,
}: TfsSrpYearRateModalComponentProps) => {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isUpdate = !!record;

  useEffect(() => {
    if (open) {
      if (record) {
        form.resetFields();
        form.setFieldsValue({
          model_year: record.model_year,
          discount_percentage: record.discount_percentage,
        });
      } else {
        form.resetFields();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
    } catch {
      // validation errors handled by antd
    }
  };

  const modalWidth = 560;

  const customHeader = (
    <div style={{
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      padding: '24px',
      margin: '-20px -24px 24px -24px',
      borderRadius: '8px 8px 0 0',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.15)',
        padding: '12px',
        borderRadius: '12px',
        display: 'flex',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(4px)'
      }}>
        <PercentageOutlined style={{ fontSize: '28px', color: '#fff' }} />
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.3px' }}>
          {isUpdate ? "Update TFS SRP Year Rate" : "Create TFS SRP Year Rate"}
        </div>
        <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
          The TFS Helper Files year discount % applied to a unit's base SRP
        </div>
      </div>
    </div>
  );

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #d6dee8',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
  };

  return (
    <Modal
      title={customHeader}
      closable={false}
      open={open}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      okText={isUpdate ? "Save Changes" : "Create Rate"}
      cancelText="Cancel"
      confirmLoading={loading}
      centered
      width={screens.xs ? "100%" : modalWidth}
      style={screens.xs ? { top: 0, padding: 0 } : { paddingBottom: 0 }}
      okButtonProps={{
        icon: <SaveOutlined />,
        size: 'large',
        style: { backgroundColor: "#1e293b", borderColor: "#1e293b", borderRadius: "8px", fontWeight: 500, padding: '0 24px' },
      }}
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: 'large',
        style: { borderRadius: "8px", fontWeight: 500, padding: '0 24px' },
      }}
    >
      {isUpdate && (
        <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #334155' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Editing Rate ID: <Text strong style={{ color: '#1e293b' }}>#{record?.tfs_srp_year_rate_id}</Text>
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical" size="large">
        <div style={cardStyle}>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="model_year"
                label={<span style={{ fontWeight: 500 }}>Model Year</span>}
                rules={[{ required: true, message: "Please enter the model year" }]}
              >
                <InputNumber
                  style={{ width: "100%", borderRadius: '6px' }}
                  placeholder="e.g. 2025"
                  min={1990}
                  max={2100}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="discount_percentage"
                label={<span style={{ fontWeight: 500 }}>Discount % (decimal)</span>}
                rules={[{ required: true, message: "Please enter the discount %" }]}
              >
                <InputNumber
                  style={{ width: "100%", borderRadius: '6px' }}
                  placeholder="e.g. 0.05"
                  min={0}
                  max={1}
                  step={0.01}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};
